/**
 * Has this user already HEARD today's morning brief on this device?
 *
 * The agent screen speaks the brief aloud at most once per user per tenant
 * day. After that it is text with its own speaker button, and nothing replays
 * it on its own — not a later visit, not a mic tap, not hands-free.
 *
 * The value stored is the tenant's calendar day ('YYYY-MM-DD', from
 * `todayCalendarDate(zone)`), never the device's: a dispatcher in Alberta on a
 * Texas tenant gets a new brief on Texas's morning, and so a new read-aloud.
 *
 * localStorage, keyed by user id, because this is a per-viewer convenience: a
 * lost value (private window, cleared storage) costs one extra read-aloud and
 * nothing else. Every accessor is wrapped, and a read that throws reports
 * "already spoken" — when the device cannot remember, staying quiet is the
 * safer guess than reading the brief out on every visit, which is the bug this
 * exists to stop.
 */

const KEY_PREFIX = 'fieldiq.briefSpoken.v1.';

/** True when `userId` has heard the brief on `today`, or when that cannot be known. */
export function hasHeardBriefToday(userId: string, today: string): boolean {
  try {
    return window.localStorage.getItem(KEY_PREFIX + userId) === today;
  } catch {
    return true;
  }
}

/** Record that `userId` heard the brief on `today`. */
export function markBriefHeard(userId: string, today: string): void {
  try {
    window.localStorage.setItem(KEY_PREFIX + userId, today);
  } catch {
    // Storage refused. The read above then reports the honest default.
  }
}
